import * as React from 'react';
import { Redirect, RouteComponentProps } from 'react-router';
import { useSelector } from 'react-redux';
import { Typography, TextInput, Button, Box, Flex, color } from '@kistech/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as authActions, selectors as authSelectors } from 'store/auth';
import { validateEmail } from 'utils/validation';
import { TextButton } from 'components/UpdateUserDrawer/styled';
import { KioskControlCenter } from 'components/KioskControlCenter';
import { useStyles } from './styled';
import { Grid, Typography as MuiTypography, Box as MuiBox } from '@mui/material';

const INVALID_EMAIL = 'Invalid email format';

const SignInView: React.FC<RouteComponentProps> = (props: RouteComponentProps) => {
  // ACTION CREATORS AND SELECTORS
  const { login, resetError } = useActions({ login: authActions.login, resetError: authActions.resetError });
  const admin = useSelector(authSelectors.selectAdmin);
  const authenticating = useSelector(authSelectors.selectAuthenticating);
  const error = useSelector(authSelectors.selectError);
  const classes = useStyles();

  // EMAIL AND PASSWORD INPUT STATES
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  // INVALID MESSAGE WHEN EMAIL INCORRECT FORMAT
  const [invalidMsg, setInvalidMsg] = React.useState<string | null>(null);

  // ON UPDATE OF ERROR - SET THE ERROR AS INVALID MESSAGE
  React.useEffect(() => {
    setInvalidMsg(error);
  }, [error]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EMAIL VALIDATION
  const emailValidationFn = async (value: string | number | undefined) => {
    // VALIDATION SCHEMA
    const schema = validateEmail(INVALID_EMAIL);

    // VALIDATE FN
    const result = await schema.validate(value).catch((err) => err);

    // IF THERE WAS ERROR MESSAGE - SET IT TO INVALID MESSAGE
    setInvalidMsg(result && result.message);
  };

  // INPUT CHANGE
  const onInputChange = (setFn: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    // IF INVALID MESSAGE EXISTS SET IT TO NULL
    if (invalidMsg) setInvalidMsg(null);
    // RESET ERROR TO INITIAL STATE
    resetError();
    // FIRES THE APPROPRIATE FN: SETEMAIL | SETPASSWORD WITH VALUE
    setFn(value);
  };

  // FORM SUBMIT
  const onLoginClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // FIRES LOGIN ASYNC FN
    login({ email, password });
  };

  // IF USER IS ALREADY AUTHENTICATED REDIRECT TO NEXT PAGE (ClientSelectView)
  if (admin) {
    if (admin.resetPasswordRequired) return <Redirect to="/resetpassword" />;
    return <Redirect to="/clients" />;
  }

  const buttonDisabled = !email.length || !password.length || !!invalidMsg;

  return (
    <Grid container direction="column" className={classes.signinViewContainer}>
      {/* LEFT OF CARD */}
      <Grid item md={12} xl={6} lg={6} className={classes.signinViewImageBlocks}>
        <Flex fullHeight bg="light-gray">
          <KioskControlCenter />
        </Flex>
      </Grid>
      {/* RIGHT OF CARD - LOGIN FORM */}
      <Grid item md={12} xl={6} lg={6} className={classes.signinViewBlocks}>
        <MuiBox className={classes.signinHeaderBox}>
          <MuiTypography className={classes.signinHeading}>Client Admin Sign In</MuiTypography>
        </MuiBox>
        <form>
          {/* EMAIL INPUT */}
          <MuiBox className={classes.signinEmail}>
            <TextInput
              onChange={(e) => onInputChange(setEmail, e.target.value)}
              isError={!!invalidMsg}
              validate={emailValidationFn}
              label="Email"
              type="email"
              placeholder="Your Email"
              fullWidth
            />
          </MuiBox>
          {/* PASSWORD INPUT */}
          <MuiBox className={classes.signinPassword}>
            <TextInput
              onChange={(e) => onInputChange(setPassword, e.target.value)}
              isError={!!invalidMsg && invalidMsg !== INVALID_EMAIL}
              label="Password"
              type="password"
              placeholder="Your Password"
              fullWidth
            />
          </MuiBox>
          <MuiBox className={classes.signinForgotPassword}>
            <TextButton type="button" onClick={() => props.history.push('/forgotpassword')}>
              Forgot your password?
            </TextButton>
          </MuiBox>
          {/* ERROR MESSAGE */}
          <Box flexOverride="none" margin="0 0 5" height={24}>
            {invalidMsg && !authenticating && (
              <Typography size={16} color={color.RED}>
                {invalidMsg}
              </Typography>
            )}
          </Box>
          {/* SUBMIT BUTTON */}
          <Button
            isDisabled={buttonDisabled}
            size="large"
            onClick={onLoginClick}
            fullWidth
            isLoading={authenticating}
            type="submit"
            className={classes.signinButton}
          >
            Sign In
          </Button>
        </form>
      </Grid>
    </Grid>
  );
};

export default SignInView;
